import type { PortableTextTextBlock } from '@sanity/types';
import { DOCUMENT, OBJECT } from '@pkg/config/sanity/schemas';
import type { WithLanguage } from './general/language';
import type { ModularBlock } from './modularContentBlocks';

export enum INPUT_TYPE {
  TEXT = 'text',
  TEXT_AREA = 'textArea',
  EMAIL = 'email',
  PHONE = 'phone',
  CHECKBOX = 'checkbox',
  RADIOS = 'radios',
  COUNTRY_SELECT = 'countrySelect',
  BLOCKER_SELECT = 'blockerSelect',
  DROPDOWN = 'dropdown',
  FILE = 'file',
  NOTE = 'note',
}

export enum CONFIRMATION_TYPE {
  MESSAGE = 'message',
  BLOCKS = 'blocks',
  PAGE = 'page',
  REDIRECT = 'redirect',
  AUTOMATED_REDIRECT = 'automatedRedirect',
}

export enum EMAIL_NOTIFICATION_TEMPLATES {
  INTERNAL_NEW_SUBMISSION_RECEIVED = 'internalNewSubmissionReceived', // This must match a template in postmark
}

export type FormFieldOptions = null | Array<{ title: string; value: { current: string; }; }>;

export interface FormFieldObject {
  _type: OBJECT.FORM_FIELD;
  _key: string;
  fieldTitle: string;
  fieldLabel: string;
  inputType: INPUT_TYPE;
  required: boolean;
  placeholder: null | string;
  options: FormFieldOptions;
  description: null | string;
  fieldId: { current: string };
  noteText?: null | Array<PortableTextTextBlock>;
  richValidationMessage?: null | Array<PortableTextTextBlock>;
  validation: {
    required?: string;
  };
}

interface FormConfirmationBase {
  /** Resolved from GROQ; preferred over legacy `type` field name in Sanity documents */
  confirmationType?: CONFIRMATION_TYPE;
  /** @deprecated Legacy Sanity field name — use confirmationType */
  type?: CONFIRMATION_TYPE;
  contentMessage?: Array<PortableTextTextBlock> | null;
  contentBlocks?: Array<ModularBlock> | null;
  contentRedirectUrl?: string | null;
}

export interface MessageFormConfirmation extends FormConfirmationBase {
  confirmationType: CONFIRMATION_TYPE.MESSAGE;
  contentMessage: Array<PortableTextTextBlock>;
}

export interface BlocksFormConfirmation extends FormConfirmationBase {
  confirmationType: CONFIRMATION_TYPE.BLOCKS;
  contentBlocks: Array<ModularBlock>;
}

export interface RedirectConfirmation extends FormConfirmationBase {
  confirmationType: CONFIRMATION_TYPE.REDIRECT;
  contentRedirectUrl: string;
}

export interface AutomatedRedirectConfirmation extends FormConfirmationBase {
  confirmationType: CONFIRMATION_TYPE.AUTOMATED_REDIRECT;
}

export type FormConfirmation =
  | MessageFormConfirmation
  | BlocksFormConfirmation
  | RedirectConfirmation
  | AutomatedRedirectConfirmation;

export interface EmailNotification {
  _type: OBJECT.FORM_NOTIFICATION_EMAIL;
  _key: string;
  toEmail?: null | string;
  fromName?: null | string;
  fromEmail?: null | string;
  replyToEmail?: null | string;
  bccEmail?: null | string;
  subject?: null | string;
  messageTemplate?: null | EMAIL_NOTIFICATION_TEMPLATES;
}

export interface StoreLead {
  _type: OBJECT.FORM_NOTIFICATION_EMAIL;
  _key: string;
  business: string;
  name: string;
  email: string;
  city: string;
  phone: string;
}

export type FormFragment = {
  _type: DOCUMENT.FRAGMENT_FORMS;
  _id: string;
  title: string;
  fields: Array<FormFieldObject>;
  submitButtonText: string;
  notifications: Array<EmailNotification>; // @todo add more notification types here
  confirmation: FormConfirmation;
} & WithLanguage;

export enum FORM_ERROR {
  API_REJECTED = 'API_REJECTED',
  CLIENT_VALIDATION = 'CLIENT_VALIDATION',
}
